import React from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import { css } from '@emotion/core';
import { Layout } from 'antd';
import { withBreakpoints } from 'react-breakpoints';
import Header from '../Header';
import Sider from '../Sider';
import { colors } from '../../constants/theme';
import { Wizard, Tutorial } from '../Onboarding';
import { ServiceDown } from '../../components/common/ServiceDown';
import MobileHeader from '../Header/MobileHeader';
import {SubscriptionNotificationPopUp, NewsPopUp} from '../../components/NewsPopUp';
import BifmSider from '../Bifm/BifmSider';
import SalefreaksHeader from '../Header/SalefreaksHeader';

const discoverPaths = {
  source: 1,
  ids: 1,
  listingSelect: 1,
  selectAccount: 1,
  strategy: 1,
  express: 1,
};

const { Content } = Layout;

const BifmUserLayout = (breakpoints, onDiscovery, currentBreakpoint, content) => {
  return (
    <Layout
      css={css`
        height: 100vh;
      `}
    >
      {breakpoints[currentBreakpoint] >= breakpoints.tabletLandscape && (
        <BifmSider
          css={css`
            overflow: hidden;
          `}
        />
      )}
      <Layout
        css={css`
          display: flex;
          flex-direction: column !important;
          background-color: ${colors.whiteLightBackground};
          ${breakpoints[currentBreakpoint] >= breakpoints.tabletLandscape
            ? 'margin-left: 118px'
            : 'margin-left: 0px'};
        `}
      >
        {breakpoints[currentBreakpoint] >= breakpoints.tabletLandscape ? (
          <Header />
        ) : (
          <MobileHeader />
        )}
        <Content
          id="tm-app-content"
          css={css`
            width: 100%;
            margin: 0 auto;
            overflow: auto;
            ${onDiscovery
              ? `
          overflow: hidden;
        `
              : `
          padding: 0 70px;
        `}
            @media (max-width: 1400px) {
              padding: 20px;
            }
          `}
        >
          {content}
        </Content>
        <Wizard />
        <Tutorial />
      </Layout>
    </Layout>
  );
};

const ConsoleLayout = (
  breakpoints,
  currentBreakpoint,
  onDiscovery,
  onOrdersOrListingsPage,
  isOnTrackinInfoPage,
  isOnSourceAccountsPage,
  content,
) => {
  return process.env.APP_NAME === 'SALEFREAKS' ? (
    <Layout
      css={css`
        display: flex;
        flex-direction: column !important;
        background-color: ${colors.whiteLightBackground};
      `}
    >
      {' '}
      {breakpoints[currentBreakpoint] >= breakpoints.tabletLandscape ? (
        <SalefreaksHeader />
      ) : (
        <MobileHeader />
      )}
      <Content
        id="tm-app-content"
        css={css`
          width: 100%;
          margin: 0 auto;
          overflow: auto;
          ${onDiscovery
            ? `
      overflow: hidden;
    `
            : `
      padding: ${
        onOrdersOrListingsPage || isOnTrackinInfoPage || isOnSourceAccountsPage
          ? '0 70px'
          : '0 calc(50vw - 683px)'
      };
    `}
          @media (max-width: 1400px) {
            padding: 20px;
          }
        `}
      >
        {content}
      </Content>
      <Wizard />
      <Tutorial />
    </Layout>
  ) : (
    <Layout
      css={css`
        height: 100vh;
      `}
    >
      {breakpoints[currentBreakpoint] >= breakpoints.tabletLandscape && (
        <Sider
          css={css`
            overflow: hidden;
          `}
        />
      )}
      <NewsPopUp />
      <SubscriptionNotificationPopUp />
      <Layout
        css={css`
          display: flex;
          flex-direction: column !important;
          background-color: ${colors.whiteLightBackground};
          ${breakpoints[currentBreakpoint] >= breakpoints.tabletLandscape
            ? 'margin-left: 118px'
            : 'margin-left: 0px'};
        `}
      >
        {breakpoints[currentBreakpoint] >= breakpoints.tabletLandscape ? (
          <Header />
        ) : (
          <MobileHeader />
        )}
        <Content
          id="tm-app-content"
          css={css`
            width: 100%;
            margin: 0 auto;
            overflow: auto;
            ${onDiscovery
              ? `
      overflow: hidden;
    `
              : `
      padding: ${
        onOrdersOrListingsPage || isOnTrackinInfoPage || isOnSourceAccountsPage
          ? '0 70px'
          : '0 calc(50vw - 683px)'
      };
    `}
            @media (max-width: 1400px) {
              padding: 20px;
            }
          `}
        >
          {content}
        </Content>
        <Wizard />
        <Tutorial />
      </Layout>
    </Layout>
  );
};

const LayoutForLoggedUsers = (
  breakpoints,
  currentBreakpoint,
  onDiscovery,
  onOrdersOrListingsPage,
  isOnTrackinInfoPage,
  isOnSourceAccountsPage,
  isBifmUser,
  content,
) => {
  return isBifmUser
    ? BifmUserLayout(breakpoints, onDiscovery, currentBreakpoint, content)
    : ConsoleLayout(
        breakpoints,
        currentBreakpoint,
        onDiscovery,
        onOrdersOrListingsPage,
        isOnTrackinInfoPage,
        isOnSourceAccountsPage,
        content,
      );
};

const discovering = () => {
  const path = window.location.pathname.split('/');

  if (path.length === 3 && path[1] === 'discover') {
    if (discoverPaths[path[2]]) {
      return true;
    }
  }
  return false;
};

const isOnOrdersOrListingsPage = () => {
  const path = window.location.pathname.split('/');
  return (
    (path[1] === 'orders' && path[2] === 'table') ||
    (path[1] === 'listings' && path[2] === 'table' && true)
  );
};

const App = ({
  children,
  sessionStore: { isLogged, isAccountUp, isBifmUser },
  location: { pathname },
  ...props
}) => {
  const onDiscovery = discovering();
  const onOrdersOrListingsPage = isOnOrdersOrListingsPage();
  const isOnTrackinInfoPage = pathname.includes('trackingInfo');
  const isOnSourceAccountsPage = pathname.includes('source-accounts');

  const { breakpoints, currentBreakpoint } = props;

  const PrivatePage = () => {
    return isLogged && isAccountUp ? (
      LayoutForLoggedUsers(
        breakpoints,
        currentBreakpoint,
        onDiscovery,
        onOrdersOrListingsPage,
        isOnTrackinInfoPage,
        isOnSourceAccountsPage,
        isBifmUser,
        children,
      )
    ) : (
      <ServiceDown />
    );
  };

  const PublicPage = () => {
    return isLogged
      ? LayoutForLoggedUsers(
          breakpoints,
          currentBreakpoint,
          onDiscovery,
          onOrdersOrListingsPage,
          isOnTrackinInfoPage,
          isOnSourceAccountsPage,
          isBifmUser,
          children,
        )
      : children;
  };

  return isOnTrackinInfoPage ? PublicPage() : PrivatePage();
};

App.propTypes = {
  children: PropTypes.node,
};

export default inject('sessionStore')(observer(withBreakpoints(App)));
